import {
  isRequirementMet,
  ModuleRequirement,
  SystemConfiguration,
} from 'app/interfaces/Config';

export type MenuPermissionGroup = 'main' | 'actions' | 'config' | 'app';

export interface MenuPermissionColumn {
  key: string;
  titleKey: string;
  group: MenuPermissionGroup;
  requiresModule?: ModuleRequirement;
}

const DOCUMENT_NUMBERING_MODULES: ModuleRequirement = [
  'isMaintenance',
  'isWorksManagement',
  'isInvoicing',
  'isPurchases',
  'isProduction',
];

export const MENU_PERMISSION_COLUMNS: MenuPermissionColumn[] = [
  { key: 'dashboard.view', titleKey: 'sidebar.dashboard', group: 'main' },
  {
    key: 'notifications.view',
    titleKey: 'users.permissions.column.notifications',
    group: 'main',
  },
  {
    key: 'preventives.view',
    titleKey: 'sidebar.preventives',
    group: 'main',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'workorders.view',
    titleKey: 'sidebar.workOrders',
    group: 'main',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'orders.view',
    titleKey: 'sidebar.orders',
    group: 'main',
    requiresModule: 'isPurchases',
  },
  {
    key: 'spareparts.view',
    titleKey: 'sidebar.spareParts',
    group: 'main',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'articles.view',
    titleKey: 'sidebar.articles',
    group: 'main',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'reports.view',
    titleKey: 'users.permissions.column.reports',
    group: 'main',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'customers.view',
    titleKey: 'sidebar.customers',
    group: 'main',
    requiresModule: ['isInvoicing', 'isProduction'],
  },
  {
    key: 'customerhistory.view',
    titleKey: 'users.permissions.column.customerhistoryview',
    group: 'main',
    requiresModule: ['isInvoicing', 'isProduction'],
  },
  {
    key: 'invoices.view',
    titleKey: 'sidebar.invoices',
    group: 'main',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'budgets.view',
    titleKey: 'users.permissions.column.budgetsview',
    group: 'main',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'assemblybudgets.view',
    titleKey: 'sidebar.assemblyBudgets',
    group: 'main',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'assemblybudgets.fieldworkorders',
    titleKey: 'assemblyBudget.fieldWorkOrders.permission',
    group: 'main',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'timetracking.view',
    titleKey: 'sidebar.clockInOut',
    group: 'main',
    requiresModule: 'isTimeTracking',
  },
  {
    key: 'datacapture.view',
    titleKey: 'sidebar.dataCapture',
    group: 'main',
    requiresModule: 'isDataCapture',
  },
  {
    key: 'production.orders.view',
    titleKey: 'users.permissions.column.productionorders',
    group: 'main',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.manufacturing.view',
    titleKey: 'users.permissions.column.productionmanufacturingview',
    group: 'main',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.planning.view',
    titleKey: 'users.permissions.column.productionplanningview',
    group: 'main',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.workcenters.view',
    titleKey: 'users.permissions.column.productionworkcentersview',
    group: 'main',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.shifts.view',
    titleKey: 'users.permissions.column.productionshiftsview',
    group: 'main',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.reporting.view',
    titleKey: 'users.permissions.column.productionreportingview',
    group: 'main',
    requiresModule: 'isProduction',
  },
  {
    key: 'workorders.bulkstate',
    titleKey: 'users.permissions.column.workordersbulkstate',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'workorders.history',
    titleKey: 'users.permissions.column.workordershistory',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'customerhistory.documents',
    titleKey: 'users.permissions.column.customerhistorydocuments',
    group: 'actions',
    requiresModule: ['isInvoicing', 'isProduction'],
  },
  {
    key: 'customerhistory.amounts',
    titleKey: 'users.permissions.column.customerhistoryamounts',
    group: 'actions',
    requiresModule: ['isInvoicing', 'isProduction'],
  },
  {
    key: 'assemblybudgets.createworkorder',
    titleKey: 'assemblyBudget.workOrders.create.permission',
    group: 'actions',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'assemblybudgets.transition',
    titleKey: 'users.permissions.column.assemblybudgetstransition',
    group: 'actions',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'documentcodes.edit',
    titleKey: 'users.permissions.column.documentcodesedit',
    group: 'actions',
    requiresModule: DOCUMENT_NUMBERING_MODULES,
  },
  {
    key: 'costcategories.edit',
    titleKey: 'users.permissions.column.costcategoriesedit',
    group: 'actions',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'specialities.edit',
    titleKey: 'users.permissions.column.specialitiesedit',
    group: 'actions',
  },
  {
    key: 'operatorroles.edit',
    titleKey: 'users.permissions.column.operatorrolesedit',
    group: 'actions',
  },
  {
    key: 'expensequestions.edit',
    titleKey: 'users.permissions.column.expensequestionsedit',
    group: 'actions',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'qualityvalidation.templates.edit',
    titleKey: 'users.permissions.column.qualitytemplatesedit',
    group: 'actions',
    requiresModule: 'isQualityValidation',
  },
  {
    key: 'qualityvalidation.answer',
    titleKey: 'users.permissions.column.qualityvalidationanswer',
    group: 'actions',
    requiresModule: 'isQualityValidation',
  },
  {
    key: 'qualityvalidation.validate',
    titleKey: 'users.permissions.column.qualityvalidationvalidate',
    group: 'actions',
    requiresModule: 'isQualityValidation',
  },
  {
    key: 'documentcodes.adjust',
    titleKey: 'users.permissions.column.documentcodesadjust',
    group: 'actions',
    requiresModule: DOCUMENT_NUMBERING_MODULES,
  },
  {
    key: 'customers.create',
    titleKey: 'users.permissions.column.customerscreate',
    group: 'actions',
    requiresModule: ['isInvoicing', 'isProduction'],
  },
  {
    key: 'customers.edit',
    titleKey: 'users.permissions.column.customersedit',
    group: 'actions',
    requiresModule: ['isInvoicing', 'isProduction'],
  },
  {
    key: 'customers.delete',
    titleKey: 'users.permissions.column.customersdelete',
    group: 'actions',
    requiresModule: ['isInvoicing', 'isProduction'],
  },
  {
    key: 'production.products.edit',
    titleKey: 'users.permissions.column.productionproductsedit',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.orders.create',
    titleKey: 'users.permissions.column.productionorderscreate',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.orders.edit',
    titleKey: 'users.permissions.column.productionordersedit',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.orders.transition',
    titleKey: 'users.permissions.column.productionorderstransition',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.preferences.edit',
    titleKey: 'users.permissions.column.productionpreferencesedit',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.manufacturing.create',
    titleKey: 'users.permissions.column.productionmanufacturingcreate',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.manufacturing.edit',
    titleKey: 'users.permissions.column.productionmanufacturingedit',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.manufacturing.transition',
    titleKey: 'users.permissions.column.productionmanufacturingtransition',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.manufacturing.cancel',
    titleKey: 'users.permissions.column.productionmanufacturingcancel',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.planning.edit',
    titleKey: 'users.permissions.column.productionplanningedit',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.planning.print',
    titleKey: 'users.permissions.column.productionplanningprint',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.workcenters.edit',
    titleKey: 'users.permissions.column.productionworkcentersedit',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.shifts.edit',
    titleKey: 'users.permissions.column.productionshiftsedit',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.reporting.create',
    titleKey: 'users.permissions.column.productionreportingcreate',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.reporting.void',
    titleKey: 'users.permissions.column.productionreportingvoid',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.reporting.erp',
    titleKey: 'users.permissions.column.productionreportingerp',
    group: 'actions',
    requiresModule: 'isProduction',
  },
  {
    key: 'workorders.create',
    titleKey: 'users.permissions.column.workorderscreate',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'workorders.edit',
    titleKey: 'users.permissions.column.workordersedit',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'workorders.delete',
    titleKey: 'users.permissions.column.workordersdelete',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'preventives.create',
    titleKey: 'users.permissions.column.preventivescreate',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'preventives.edit',
    titleKey: 'users.permissions.column.preventivesedit',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'preventives.delete',
    titleKey: 'users.permissions.column.preventivesdelete',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'assets.create',
    titleKey: 'users.permissions.column.assetscreate',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'assets.edit',
    titleKey: 'users.permissions.column.assetsedit',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'assets.delete',
    titleKey: 'users.permissions.column.assetsdelete',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'spareparts.create',
    titleKey: 'users.permissions.column.sparepartscreate',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'spareparts.edit',
    titleKey: 'users.permissions.column.sparepartsedit',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'spareparts.delete',
    titleKey: 'users.permissions.column.sparepartsdelete',
    group: 'actions',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'orders.create',
    titleKey: 'users.permissions.column.orderscreate',
    group: 'actions',
    requiresModule: 'isPurchases',
  },
  {
    key: 'orders.edit',
    titleKey: 'users.permissions.column.ordersedit',
    group: 'actions',
    requiresModule: 'isPurchases',
  },
  {
    key: 'orders.delete',
    titleKey: 'users.permissions.column.ordersdelete',
    group: 'actions',
    requiresModule: 'isPurchases',
  },
  {
    key: 'operators.create',
    titleKey: 'users.permissions.column.operatorscreate',
    group: 'actions',
  },
  {
    key: 'operators.edit',
    titleKey: 'users.permissions.column.operatorsedit',
    group: 'actions',
  },
  {
    key: 'operators.delete',
    titleKey: 'users.permissions.column.operatorsdelete',
    group: 'actions',
  },
  {
    key: 'users.create',
    titleKey: 'users.permissions.column.userscreate',
    group: 'actions',
  },
  {
    key: 'users.edit',
    titleKey: 'users.permissions.column.usersedit',
    group: 'actions',
  },
  {
    key: 'users.delete',
    titleKey: 'users.permissions.column.usersdelete',
    group: 'actions',
  },
  {
    key: 'invoices.create',
    titleKey: 'users.permissions.column.invoicescreate',
    group: 'actions',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'invoices.edit',
    titleKey: 'users.permissions.column.invoicesedit',
    group: 'actions',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'invoices.delete',
    titleKey: 'users.permissions.column.invoicesdelete',
    group: 'actions',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'budgets.edit',
    titleKey: 'users.permissions.column.budgetsedit',
    group: 'actions',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'budgets.transition',
    titleKey: 'users.permissions.column.budgetstransition',
    group: 'actions',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'articles.create',
    titleKey: 'users.permissions.column.articlescreate',
    group: 'actions',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'articles.edit',
    titleKey: 'users.permissions.column.articlesedit',
    group: 'actions',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'articles.delete',
    titleKey: 'users.permissions.column.articlesdelete',
    group: 'actions',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'timetracking.edit',
    titleKey: 'users.permissions.column.timetrackingedit',
    group: 'actions',
    requiresModule: 'isTimeTracking',
  },
  {
    key: 'config.edit',
    titleKey: 'users.permissions.column.configedit',
    group: 'actions',
  },
  { key: 'config.view', titleKey: 'sidebar.config', group: 'config' },
  {
    key: 'assets.view',
    titleKey: 'sidebar.assets',
    group: 'config',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'providers.view',
    titleKey: 'sidebar.providers',
    group: 'config',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'warehouse.view',
    titleKey: 'sidebar.warehouse',
    group: 'config',
    requiresModule: 'isMaintenance',
  },
  {
    key: 'families.view',
    titleKey: 'sidebar.families',
    group: 'config',
    requiresModule: ['isMaintenance', 'isWorksManagement'],
  },
  {
    key: 'holidays.view',
    titleKey: 'sidebar.holidays',
    group: 'config',
    requiresModule: 'isHolidays',
  },
  {
    key: 'accounts.view',
    titleKey: 'sidebar.accounts',
    group: 'config',
    requiresModule: 'isPurchases',
  },
  { key: 'operators.view', titleKey: 'sidebar.operators', group: 'config' },
  { key: 'users.view', titleKey: 'sidebar.users', group: 'config' },
  { key: 'system.view', titleKey: 'sidebar.system', group: 'config' },
  {
    key: 'documentcodes.view',
    titleKey: 'users.permissions.column.documentcodesview',
    group: 'config',
    requiresModule: DOCUMENT_NUMBERING_MODULES,
  },
  {
    key: 'costcategories.view',
    titleKey: 'users.permissions.column.costcategoriesview',
    group: 'config',
    requiresModule: 'isWorksManagement',
  },
  {
    key: 'specialities.view',
    titleKey: 'users.permissions.column.specialitiesview',
    group: 'config',
  },
  {
    key: 'operatorroles.view',
    titleKey: 'users.permissions.column.operatorrolesview',
    group: 'config',
  },
  {
    key: 'production.products.view',
    titleKey: 'users.permissions.column.productionproducts',
    group: 'config',
    requiresModule: 'isProduction',
  },
  {
    key: 'production.configure',
    titleKey: 'users.permissions.column.productionconfigure',
    group: 'config',
    requiresModule: 'isProduction',
  },
  {
    key: 'expensequestions.view',
    titleKey: 'users.permissions.column.expensequestionsview',
    group: 'config',
    requiresModule: 'isInvoicing',
  },
  {
    key: 'workflows.configure',
    titleKey: 'users.permissions.column.workflowsconfigure',
    group: 'config',
  },
  {
    key: 'qualityvalidation.view',
    titleKey: 'users.permissions.column.qualityvalidationview',
    group: 'config',
    requiresModule: 'isQualityValidation',
  },
  {
    key: 'app.access',
    titleKey: 'users.permissions.column.appaccess',
    group: 'app',
  },
  {
    key: 'app.workorders',
    titleKey: 'users.permissions.column.appworkorders',
    group: 'app',
  },
  {
    key: 'app.workorderhistory',
    titleKey: 'users.permissions.column.appworkorderhistory',
    group: 'app',
  },
  {
    key: 'app.timetracking',
    titleKey: 'users.permissions.column.apptimetracking',
    group: 'app',
    requiresModule: 'isTimeTracking',
  },
  {
    key: 'app.vacations',
    titleKey: 'users.permissions.column.appvacations',
    group: 'app',
    requiresModule: 'isHolidays',
  },
];

export const CONFIG_PARENT_KEY = 'config.view';
export const USERS_VIEW_KEY = 'users.view';
export const USERS_EDIT_KEY = 'users.edit';

const CONFIG_CHILD_KEYS = MENU_PERMISSION_COLUMNS.filter(
  column => column.group === 'config' && column.key !== CONFIG_PARENT_KEY
).map(column => column.key);

const withParent = (parent: string, children: string[]) =>
  children.map(child => [child, parent] as const);

export const PERMISSION_PARENTS: Record<string, string> = Object.fromEntries([
  ...withParent('workorders.view', [
    'workorders.create',
    'workorders.edit',
    'workorders.delete',
    'workorders.bulkstate',
    'workorders.history',
  ]),
  ...withParent('customerhistory.view', [
    'customerhistory.documents',
    'customerhistory.amounts',
  ]),
  ...withParent('preventives.view', [
    'preventives.create',
    'preventives.edit',
    'preventives.delete',
  ]),
  ...withParent('spareparts.view', [
    'spareparts.create',
    'spareparts.edit',
    'spareparts.delete',
  ]),
  ...withParent('orders.view', [
    'orders.create',
    'orders.edit',
    'orders.delete',
  ]),
  ...withParent('assets.view', [
    'assets.create',
    'assets.edit',
    'assets.delete',
  ]),
  ...withParent('operators.view', [
    'operators.create',
    'operators.edit',
    'operators.delete',
  ]),
  ...withParent('users.view', ['users.create', 'users.edit', 'users.delete']),
  ...withParent('customers.view', [
    'customers.create',
    'customers.edit',
    'customers.delete',
  ]),
  ...withParent('invoices.view', [
    'invoices.create',
    'invoices.edit',
    'invoices.delete',
  ]),
  ...withParent('articles.view', [
    'articles.create',
    'articles.edit',
    'articles.delete',
  ]),
  ...withParent('timetracking.view', ['timetracking.edit']),
  ...withParent('budgets.view', ['budgets.edit', 'budgets.transition']),
  ...withParent('assemblybudgets.view', [
    'assemblybudgets.transition',
    'assemblybudgets.createworkorder',
    'assemblybudgets.fieldworkorders',
  ]),
  ...withParent('documentcodes.view', [
    'documentcodes.edit',
    'documentcodes.adjust',
  ]),
  ...withParent('costcategories.view', ['costcategories.edit']),
  ...withParent('specialities.view', ['specialities.edit']),
  ...withParent('expensequestions.view', ['expensequestions.edit']),
  ...withParent('operatorroles.view', ['operatorroles.edit']),
  ...withParent('app.access', [
    'app.workorders',
    'app.workorderhistory',
    'app.timetracking',
    'app.vacations',
  ]),
  ...withParent('production.products.view', ['production.products.edit']),
  ...withParent('production.orders.view', [
    'production.orders.create',
    'production.orders.edit',
    'production.orders.transition',
  ]),
  ...withParent('production.configure', ['production.preferences.edit']),
  ...withParent('production.workcenters.view', ['production.workcenters.edit']),
  ...withParent('production.shifts.view', ['production.shifts.edit']),
  ...withParent('production.manufacturing.view', [
    'production.manufacturing.create',
    'production.manufacturing.edit',
    'production.manufacturing.transition',
    'production.manufacturing.cancel',
  ]),
  ...withParent('production.reporting.view', [
    'production.reporting.create',
    'production.reporting.void',
    'production.reporting.erp',
  ]),
  ...withParent('production.planning.view', [
    'production.planning.edit',
    'production.planning.print',
  ]),
  ...withParent(CONFIG_PARENT_KEY, ['config.edit', ...CONFIG_CHILD_KEYS]),
]);

export const ancestorsOf = (key: string): string[] => {
  const parent = PERMISSION_PARENTS[key];
  return parent ? [parent, ...ancestorsOf(parent)] : [];
};

export const descendantsOf = (key: string): string[] =>
  Object.keys(PERMISSION_PARENTS)
    .filter(child => PERMISSION_PARENTS[child] === key)
    .flatMap(child => [child, ...descendantsOf(child)]);

export const normalizePermissions = (permissions: string[]): string[] => {
  const granted = new Set(permissions);
  return Array.from(granted).filter(key =>
    ancestorsOf(key).every(ancestor => granted.has(ancestor))
  );
};

export const grantPermission = (permissions: string[], key: string): string[] =>
  normalizePermissions([...permissions, key, ...ancestorsOf(key)]);

export const grantPermissionIfParentPresent = (
  permissions: string[],
  key: string
): string[] =>
  permissions.includes(key) || missingParentOf(key, permissions)
    ? permissions
    : normalizePermissions([...permissions, key]);

export const revokePermission = (
  permissions: string[],
  key: string
): string[] => {
  const removed = new Set([key, ...descendantsOf(key)]);
  return normalizePermissions(permissions.filter(item => !removed.has(item)));
};

export const togglePermission = (
  permissions: string[],
  key: string
): string[] =>
  permissions.includes(key)
    ? revokePermission(permissions, key)
    : grantPermission(permissions, key);

export const missingParentOf = (
  key: string,
  permissions: string[]
): string | undefined => {
  const parent = PERMISSION_PARENTS[key];
  return parent && !permissions.includes(parent) ? parent : undefined;
};

export const SELF_LOCKED_KEYS = [
  USERS_VIEW_KEY,
  USERS_EDIT_KEY,
  ...ancestorsOf(USERS_VIEW_KEY),
];

export const MODULE_BY_PERMISSION_KEY: Record<string, ModuleRequirement> =
  MENU_PERMISSION_COLUMNS.reduce<Record<string, ModuleRequirement>>(
    (accumulator, column) => {
      if (column.requiresModule) {
        accumulator[column.key] = column.requiresModule;
      }
      return accumulator;
    },
    {}
  );

export const filterColumnsByModules = (
  columns: MenuPermissionColumn[],
  config: SystemConfiguration | undefined
): MenuPermissionColumn[] =>
  columns.filter(column => isRequirementMet(config, column.requiresModule));

export const permissionTitleKeyOf = (key: string): string | undefined =>
  MENU_PERMISSION_COLUMNS.find(column => column.key === key)?.titleKey;
